import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import {
    addBrandOpsStrike,
    approveBrandOpsContract,
    dispatchBrandOpsContract,
    downloadBrandOpsContractPdf,
    fetchContractClarifications,
    freezeBrandOpsContract,
    getBrandOpsContract,
    lockBrandOpsEscrow,
    payoutBrandOpsEscrow,
    releaseBrandOpsEscrow,
    renewBrandOpsContract,
    replyToContractClarification,
    voidBrandOpsContract,
} from '../../../redux/brandOps/actions';
import {
    brandOpsActiveContractSelector,
    brandOpsContractClarificationsSelector,
    brandOpsDownloadingPdfSelector,
    brandOpsFetchingClarificationsSelector,
    brandOpsFetchingContractSelector,
    brandOpsMutatingContractSelector,
    brandOpsMutatingEscrowSelector,
    brandOpsPendingClarificationCountSelector,
    brandOpsPendingContractSignedCountSelector,
    brandOpsPendingContractDeclinedCountSelector,
    brandOpsReplyingToClarificationSelector,
} from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    ManageSurface,
    MANAGE_PAGE_SX,
    fmtCurrency,
    useBrandManageGate,
} from './manageShared';
import ContractEscrowCard from './ContractEscrowCard';

function fmtDate(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const day = d.getDate();
    const suffix =
        day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
            ? 'nd'
            : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${month} ${day}${suffix}, ${year}`;
}

function fmtDateTime(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const day = d.getDate();
    const suffix =
        day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
            ? 'nd'
            : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${month} ${day}${suffix}, ${year} · ${time}`;
}

export default function BrandManageContractDetailPage() {
    const { contractId = '' } = useParams();
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const nav = useNavigate();
    const { brandId } = useBrandManageGate();
    const contract = useSelector(brandOpsActiveContractSelector);
    const fetching = useSelector(brandOpsFetchingContractSelector);
    const downloadingPdf = useSelector(brandOpsDownloadingPdfSelector);
    const mutatingEscrow = useSelector(brandOpsMutatingEscrowSelector);
    const mutatingContract = useSelector(brandOpsMutatingContractSelector);
    const clarifications = useSelector(brandOpsContractClarificationsSelector);
    const fetchingClarifications = useSelector(brandOpsFetchingClarificationsSelector);
    const replyingToClarification = useSelector(brandOpsReplyingToClarificationSelector);
    const pendingClarificationCount = useSelector(brandOpsPendingClarificationCountSelector);
    const pendingContractSignedCount = useSelector(brandOpsPendingContractSignedCountSelector);
    const pendingContractDeclinedCount = useSelector(brandOpsPendingContractDeclinedCountSelector);
    const [strikeReason, setStrikeReason] = useState('');
    const [confirmAction, setConfirmAction] = useState<'freeze' | 'void' | null>(null);
    const [replyTexts, setReplyTexts] = useState<Record<string, string>>({});

    useEffect(() => {
        if (brandId && contractId) dispatch(getBrandOpsContract({ brandId, contractId }));
    }, [dispatch, brandId, contractId]);

    useEffect(() => {
        if (brandId && contractId) dispatch(fetchContractClarifications({ brandId, contractId }));
    }, [dispatch, brandId, contractId, pendingClarificationCount]);

    // Toast when manager signs
    useEffect(() => {
        if (pendingContractSignedCount > 0) {
            showToastMessage('Manager has signed the contract', 'success');
            dispatch(getBrandOpsContract({ brandId: brandId!, contractId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingContractSignedCount]);

    // Toast when manager declines
    useEffect(() => {
        if (pendingContractDeclinedCount > 0) {
            showToastMessage('Manager declined the contract offer', 'error');
            dispatch(getBrandOpsContract({ brandId: brandId!, contractId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingContractDeclinedCount]);

    const onDispatch = async () => {
        if (!brandId || !contractId) return;
        try {
            await dispatchP(dispatchBrandOpsContract({ brandId, contractId }));
            showToastMessage('Contract dispatched for signature', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Dispatch failed', 'error');
        }
    };

    const onDownloadPdf = async () => {
        if (!brandId || !contractId) return;
        try {
            await dispatchP(downloadBrandOpsContractPdf({ brandId, contractId }));
            showToastMessage('PDF downloaded', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'PDF download failed', 'error');
        }
    };

    const runEscrow = async (action: 'lock' | 'payout' | 'release', periodId?: string) => {
        if (!brandId || !contractId || !periodId) {
            showToastMessage('Period id missing — refresh contract', 'error');
            return;
        }
        try {
            const fn =
                action === 'lock'
                    ? lockBrandOpsEscrow
                    : action === 'payout'
                    ? payoutBrandOpsEscrow
                    : releaseBrandOpsEscrow;
            await dispatchP(fn({ brandId, contractId, periodId }));
            showToastMessage(
                action === 'lock' ? 'Escrow locked' : action === 'payout' ? 'Payout queued' : 'Escrow released',
                'success',
            );
        } catch (err: any) {
            showToastMessage(err?.message || `Escrow ${action} failed`, 'error');
        }
    };

    const onRenew = async () => {
        if (!brandId || !contractId) return;
        try {
            const renewed: any = await dispatchP(renewBrandOpsContract({ brandId, contractId }));
            showToastMessage('Contract renewed as a new draft', 'success');
            const newId = renewed?._id || renewed?.id;
            if (newId) nav(ROUTES.BRAND_MANAGE_CONTRACT_DETAIL.replace(':contractId', newId));
        } catch (err: any) {
            showToastMessage(err?.message || 'Renewal failed', 'error');
        }
    };

    const onApprove = async () => {
        if (!brandId || !contractId) return;
        try {
            await dispatchP(approveBrandOpsContract({ brandId, contractId }));
            showToastMessage('Contract approved — manager is now active', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Approval failed', 'error');
        }
    };

    const onConfirmAction = async () => {
        if (!brandId || !contractId || !confirmAction) return;
        try {
            const action = confirmAction === 'freeze' ? freezeBrandOpsContract : voidBrandOpsContract;
            await dispatchP(action({ brandId, contractId }));
            showToastMessage(confirmAction === 'freeze' ? 'Contract paused' : 'Contract voided', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || `Could not ${confirmAction} contract`, 'error');
        } finally {
            setConfirmAction(null);
        }
    };

    const onAddStrike = async () => {
        if (!brandId || !contractId || !strikeReason.trim()) return;
        try {
            await dispatchP(addBrandOpsStrike({ brandId, contractId, reason: strikeReason.trim() }));
            setStrikeReason('');
            showToastMessage('Strike recorded', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not add strike', 'error');
        }
    };

    const onSendReply = async (clarificationId: string) => {
        if (!brandId || !contractId) return;
        const reply = (replyTexts[clarificationId] || '').trim();
        if (!reply) return;
        try {
            await dispatchP(replyToContractClarification({ brandId, contractId, clarificationId, reply }));
            setReplyTexts(prev => ({ ...prev, [clarificationId]: '' }));
            showToastMessage('Reply sent', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not send reply', 'error');
        }
    };

    const strikes = contract?.strikes?.count ?? 0;
    const escrow = contract?.escrow_periods || [];
    const unansweredCount = clarifications.filter(c => !c.is_answered).length;

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title={contract?.title || 'Contract'}
                    subtitle={contract?.manager_email || 'Manager agreement details'}
                    back={ROUTES.BRAND_MANAGE_CONTRACTS}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {contract ? (
                                <ManageGoldButton onClick={onDownloadPdf} disabled={downloadingPdf}>
                                    {downloadingPdf ? 'Downloading…' : 'Download PDF'}
                                </ManageGoldButton>
                            ) : null}
                            {contract?.status === 'draft' || contract?.status === 'pending_signature' ? (
                                <ManageGoldButton onClick={onDispatch}>Dispatch</ManageGoldButton>
                            ) : null}
                            {contract?.status === 'awaiting_brand_approval' ? (
                                <ManageGoldButton onClick={onApprove} disabled={mutatingContract}>
                                    {mutatingContract ? 'Approving…' : 'Approve Contract'}
                                </ManageGoldButton>
                            ) : null}
                            {contract?.status === 'pending_signature' ? (
                                <Button
                                    component={RouterLink}
                                    to={ROUTES.BRAND_MANAGE_CONTRACT_SIGN.replace(':contractId', contractId)}
                                    sx={{ color: BRAND_LUXE_PRIMARY, textTransform: 'none' }}>
                                    Sign
                                </Button>
                            ) : null}
                            {contract?.internal_status === 'active' ? (
                                <ManageGoldButton
                                    onClick={() => setConfirmAction('freeze')}
                                    disabled={mutatingContract}
                                    sx={{
                                        bgcolor: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        color: BRAND_LUXE_MUTED,
                                    }}>
                                    Pause
                                </ManageGoldButton>
                            ) : null}
                            {!['ended', 'voided'].includes(contract?.internal_status || '') ? (
                                <ManageGoldButton
                                    onClick={() => setConfirmAction('void')}
                                    disabled={mutatingContract}
                                    sx={{
                                        bgcolor: 'transparent',
                                        border: '1px solid rgba(255,50,50,0.3)',
                                        color: '#ff7351',
                                    }}>
                                    Void
                                </ManageGoldButton>
                            ) : null}
                            {['ended', 'voided'].includes(contract?.internal_status || '') ? (
                                <ManageGoldButton onClick={onRenew} disabled={mutatingContract}>
                                    {mutatingContract ? 'Renewing…' : 'Renew contract'}
                                </ManageGoldButton>
                            ) : null}
                        </Box>
                    }
                />
                {contract?.status === 'awaiting_brand_approval' ? (
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            bgcolor: 'rgba(255,201,101,0.08)',
                            color: '#fff',
                            border: '1px solid rgba(255,201,101,0.25)',
                            '& .MuiAlert-icon': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        <strong>Awaiting your approval.</strong> The manager has signed this contract. Review the terms
                        below and click <strong>Approve Contract</strong> above to grant them access.
                    </Alert>
                ) : null}
                {['ended', 'voided'].includes(contract?.internal_status || '') ? (
                    <Alert
                        severity="info"
                        action={
                            <ManageGoldButton onClick={onRenew} disabled={mutatingContract}>
                                {mutatingContract ? 'Renewing…' : 'Renew'}
                            </ManageGoldButton>
                        }
                        sx={{
                            mb: 2,
                            bgcolor: 'rgba(255,255,255,0.05)',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.12)',
                            '& .MuiAlert-icon': { color: BRAND_LUXE_MUTED },
                        }}>
                        This contract has{' '}
                        {contract?.internal_status === 'voided' || contract?.internal_status === 'void'
                            ? 'been voided'
                            : 'ended'}
                        . Renew to start a new contract with the same manager using the existing terms as a starting
                        point.
                    </Alert>
                ) : null}
                {contract?.internal_status === 'payment_grace' ? (
                    <Alert
                        severity="warning"
                        action={
                            <ManageGoldButton
                                component={RouterLink as any}
                                to={`${
                                    ROUTES.BRAND_WALLET
                                }?topup=1&reason=manager_escrow&contractId=${encodeURIComponent(contractId)}`}>
                                Top up wallet
                            </ManageGoldButton>
                        }
                        sx={{
                            mb: 2,
                            bgcolor: 'rgba(255,201,101,0.08)',
                            color: '#fff',
                            border: '1px solid rgba(255,201,101,0.25)',
                            '& .MuiAlert-icon': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        <strong>Payment grace.</strong> Escrow could not be locked from your brand wallet. Top up to
                        keep the manager contract active
                        {contract.payment_grace_until
                            ? ` — grace ends ${fmtDateTime(contract.payment_grace_until)}`
                            : ''}
                        .
                    </Alert>
                ) : null}
                {fetching && !contract ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {/* Left card skeleton */}
                        <Box
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.07)',
                                bgcolor: 'rgba(255,255,255,0.02)',
                            }}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.5,
                                    pb: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(255,201,101,0.08) 0%, rgba(251,146,60,0.05) 100%)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Skeleton
                                    variant="text"
                                    width={140}
                                    height={20}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.07)' }}
                                />
                            </Box>
                            <Box sx={{ px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Skeleton
                                    variant="rounded"
                                    width={64}
                                    height={22}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 10 }}
                                />
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(255,255,255,0.03)',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                    }}>
                                    <Skeleton
                                        variant="text"
                                        width={60}
                                        height={13}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 0.5 }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width={100}
                                        height={28}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.09)' }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width={80}
                                        height={13}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.05)', mt: 0.5 }}
                                    />
                                </Box>
                                {[180, 140, 160].map((w, i) => (
                                    <Skeleton
                                        key={i}
                                        variant="text"
                                        width={w}
                                        height={15}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                                    />
                                ))}
                                <Skeleton
                                    variant="rounded"
                                    height={64}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2, mt: 1 }}
                                />
                            </Box>
                        </Box>
                        {/* Right card skeleton */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.07)',
                                    bgcolor: 'rgba(255,255,255,0.02)',
                                }}>
                                <Box
                                    sx={{
                                        px: 2.5,
                                        pt: 2.5,
                                        pb: 2,
                                        background:
                                            'linear-gradient(135deg, rgba(255,201,101,0.08) 0%, rgba(251,146,60,0.05) 100%)',
                                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    }}>
                                    <Skeleton
                                        variant="text"
                                        width={100}
                                        height={20}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.07)' }}
                                    />
                                </Box>
                                <Box sx={{ px: 2.5, py: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Skeleton
                                            variant="circular"
                                            width={52}
                                            height={52}
                                            sx={{ bgcolor: 'rgba(255,255,255,0.07)' }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton
                                                variant="text"
                                                width="40%"
                                                height={15}
                                                sx={{ bgcolor: 'rgba(255,255,255,0.07)' }}
                                            />
                                            <Skeleton
                                                variant="text"
                                                width="60%"
                                                height={13}
                                                sx={{ bgcolor: 'rgba(255,255,255,0.05)', mt: 0.5 }}
                                            />
                                        </Box>
                                    </Box>
                                    <Skeleton
                                        variant="rounded"
                                        height={36}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 }}
                                    />
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.07)',
                                    bgcolor: 'rgba(255,255,255,0.02)',
                                }}>
                                <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                    <Skeleton
                                        variant="text"
                                        width={120}
                                        height={20}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.07)' }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width="80%"
                                        height={13}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.04)', mt: 0.5 }}
                                    />
                                </Box>
                                <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {[0, 1].map(i => (
                                        <Box
                                            key={i}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                border: '1px solid rgba(255,255,255,0.06)',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}>
                                            <Box>
                                                <Skeleton
                                                    variant="text"
                                                    width={140}
                                                    height={15}
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.07)' }}
                                                />
                                                <Skeleton
                                                    variant="text"
                                                    width={100}
                                                    height={13}
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.05)', mt: 0.5 }}
                                                />
                                            </Box>
                                            <Skeleton
                                                variant="rounded"
                                                width={72}
                                                height={24}
                                                sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 10 }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                ) : !contract ? (
                    <Typography sx={{ color: BRAND_LUXE_MUTED }}>Contract not found or API unavailable.</Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {/* ── Contract Details Card ── */}
                        <ManageSurface sx={{ p: 0, overflow: 'hidden' }}>
                            {/* Coloured header */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.5,
                                    pb: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(255,201,101,0.1) 0%, rgba(251,146,60,0.07) 100%)',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(255,201,101,0.15)',
                                            display: 'flex',
                                        }}>
                                        <ArticleOutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18 }} />
                                    </Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 15 }}>
                                        Contract Details
                                    </Typography>
                                </Box>
                                {/* Status badges row */}
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    {(() => {
                                        const s = contract.status;
                                        const isGood = s === 'signed';
                                        const isBad = s === 'void' || s === 'voided';
                                        return (
                                            <Box
                                                sx={{
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: 10,
                                                    bgcolor: isGood
                                                        ? 'rgba(52,211,153,0.12)'
                                                        : isBad
                                                        ? 'rgba(239,68,68,0.12)'
                                                        : 'rgba(255,201,101,0.12)',
                                                    border: `1px solid ${
                                                        isGood
                                                            ? 'rgba(52,211,153,0.3)'
                                                            : isBad
                                                            ? 'rgba(239,68,68,0.3)'
                                                            : 'rgba(255,201,101,0.3)'
                                                    }`,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        color: isGood
                                                            ? '#34d399'
                                                            : isBad
                                                            ? '#f87171'
                                                            : BRAND_LUXE_PRIMARY,
                                                        fontSize: 11,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    }}>
                                                    {s.replace(/_/g, ' ')}
                                                </Typography>
                                            </Box>
                                        );
                                    })()}
                                    {contract.internal_status === 'paused' && (
                                        <Box
                                            sx={{
                                                px: 1.25,
                                                py: 0.35,
                                                borderRadius: 10,
                                                bgcolor: 'rgba(239,68,68,0.1)',
                                                border: '1px solid rgba(239,68,68,0.25)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: '#f87171',
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                paused
                                            </Typography>
                                        </Box>
                                    )}
                                    {contract.internal_status === 'payment_grace' && (
                                        <Box
                                            sx={{
                                                px: 1.25,
                                                py: 0.35,
                                                borderRadius: 10,
                                                bgcolor: 'rgba(255,201,101,0.1)',
                                                border: '1px solid rgba(255,201,101,0.25)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: BRAND_LUXE_PRIMARY,
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                payment grace
                                            </Typography>
                                        </Box>
                                    )}
                                    {contract.void_at && (
                                        <Box
                                            sx={{
                                                px: 1.25,
                                                py: 0.35,
                                                borderRadius: 10,
                                                bgcolor: 'rgba(239,68,68,0.1)',
                                                border: '1px solid rgba(239,68,68,0.25)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: '#f87171',
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                voided
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Body */}
                            <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Rate highlight */}
                                <Box
                                    sx={{
                                        p: 1.75,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(255,201,101,0.06)',
                                        border: '1px solid rgba(255,201,101,0.15)',
                                    }}>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mb: 0.25 }}>
                                        Rate
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: BRAND_LUXE_PRIMARY,
                                            fontSize: 22,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            lineHeight: 1.2,
                                        }}>
                                        {fmtCurrency(contract.fee, contract.currency)}
                                    </Typography>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mt: 0.25 }}>
                                        per {contract.cadence}
                                    </Typography>
                                </Box>

                                {/* Deliverables */}
                                {contract.deliverables && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: BRAND_LUXE_MUTED,
                                                fontSize: 11,
                                                mb: 0.5,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}>
                                            Deliverables
                                        </Typography>
                                        <Typography
                                            sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.55 }}>
                                            {contract.deliverables}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Divider */}
                                <Box
                                    sx={{
                                        borderTop: '1px solid rgba(255,255,255,0.05)',
                                        pt: 1.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.75,
                                    }}>
                                    {contract.pause_reason && (
                                        <Typography sx={{ color: '#f87171', fontSize: 12 }}>
                                            Pause reason: {contract.pause_reason}
                                        </Typography>
                                    )}
                                    {contract.payment_grace_until && (
                                        <Typography sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 12 }}>
                                            Grace until: {fmtDateTime(contract.payment_grace_until)}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: BRAND_LUXE_MUTED }} />
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                            Signed: {fmtDateTime(contract.signed_at)}
                                        </Typography>
                                    </Box>
                                    {contract.signature?.typed_name && (
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                            Signer:{' '}
                                            <span style={{ color: '#fff' }}>{contract.signature.typed_name}</span>
                                        </Typography>
                                    )}
                                    {contract.duration_periods != null && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <HourglassBottomOutlinedIcon
                                                sx={{ fontSize: 13, color: BRAND_LUXE_MUTED }}
                                            />
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                                Duration:{' '}
                                                <span style={{ color: '#fff' }}>
                                                    {contract.periods_completed ?? 0} / {contract.duration_periods}{' '}
                                                    {contract.cadence === 'weekly'
                                                        ? 'weeks'
                                                        : contract.cadence === 'monthly'
                                                        ? 'months'
                                                        : 'deliverables'}
                                                </span>
                                                {(contract.periods_completed ?? 0) >= contract.duration_periods && (
                                                    <span style={{ color: '#34d399', marginLeft: 6 }}>· complete</span>
                                                )}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Signature image */}
                                {contract.signature?.image_data_url && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography
                                            sx={{
                                                color: BRAND_LUXE_MUTED,
                                                fontSize: 11,
                                                mb: 0.75,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}>
                                            Signature
                                        </Typography>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                display: 'inline-block',
                                            }}>
                                            <Box
                                                component="img"
                                                src={contract.signature.image_data_url}
                                                alt="Signature"
                                                sx={{ maxWidth: 200, display: 'block' }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </ManageSurface>

                        {/* ── Right column ── */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Strikes Card */}
                            <ManageSurface sx={{ p: 0, overflow: 'hidden' }}>
                                <Box
                                    sx={{
                                        px: 2.5,
                                        pt: 2,
                                        pb: 1.75,
                                        background:
                                            strikes > 0
                                                ? 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(251,146,60,0.07) 100%)'
                                                : 'linear-gradient(135deg, rgba(52,211,153,0.08) 0%, rgba(6,182,212,0.05) 100%)',
                                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: 1.5,
                                            bgcolor: strikes > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(52,211,153,0.15)',
                                            display: 'flex',
                                        }}>
                                        <WarningAmberOutlinedIcon
                                            sx={{ color: strikes > 0 ? '#f87171' : '#34d399', fontSize: 18 }}
                                        />
                                    </Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                                        Strikes
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 2.5, py: 2 }}>
                                    {/* Strike counter */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 1.5 }}>
                                        <Typography
                                            sx={{
                                                color: strikes === 0 ? '#34d399' : strikes >= 3 ? '#f87171' : '#fb923c',
                                                fontSize: 52,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                lineHeight: 1,
                                            }}>
                                            {strikes}
                                        </Typography>
                                        <Box sx={{ pb: 0.75 }}>
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                                                strikes
                                            </Typography>
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                threshold: {contract.missed_post_threshold ?? '—'} missed
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Strike history */}
                                    {(contract.strikes?.history || []).slice(0, 5).map((h, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 0.6,
                                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    bgcolor: '#fb923c',
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, flex: 1 }}>
                                                {h.reason || 'strike'}
                                            </Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 10 }}>
                                                {fmtDate(h.at)}
                                            </Typography>
                                        </Box>
                                    ))}

                                    {/* Add strike */}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Reason for strike…"
                                            value={strikeReason}
                                            onChange={e => setStrikeReason(e.target.value)}
                                            sx={{
                                                input: { color: '#fff', fontSize: 13 },
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: 'rgba(255,255,255,0.03)',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                                    '&.Mui-focused fieldset': { borderColor: '#fb923c' },
                                                },
                                                '& .MuiInputBase-input::placeholder': { color: BRAND_LUXE_MUTED },
                                            }}
                                        />
                                        <ManageGoldButton
                                            onClick={onAddStrike}
                                            disabled={mutatingEscrow || !strikeReason.trim()}
                                            sx={{
                                                flexShrink: 0,
                                                bgcolor: 'rgba(251,146,60,0.15)',
                                                color: '#fb923c',
                                                border: '1px solid rgba(251,146,60,0.3)',
                                                '&:hover': { bgcolor: 'rgba(251,146,60,0.25)' },
                                            }}>
                                            + Strike
                                        </ManageGoldButton>
                                    </Box>
                                </Box>
                            </ManageSurface>

                            <ContractEscrowCard periods={escrow} busy={mutatingEscrow} onAction={runEscrow} />
                        </Box>

                        {/* ── Clarifications ── */}
                        {fetchingClarifications ? (
                            <ManageSurface sx={{ gridColumn: '1 / -1', p: 0, overflow: 'hidden' }}>
                                <Box
                                    sx={{ px: 2.5, pt: 2, pb: 1.75, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                    <Skeleton
                                        variant="text"
                                        width={160}
                                        height={20}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                                    />
                                </Box>
                                <Box sx={{ px: 2.5, py: 2 }}>
                                    <Skeleton
                                        variant="rectangular"
                                        height={60}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1.5 }}
                                    />
                                </Box>
                            </ManageSurface>
                        ) : clarifications.length > 0 ? (
                            <ManageSurface sx={{ gridColumn: '1 / -1', p: 0, overflow: 'hidden' }}>
                                {/* Header */}
                                <Box
                                    sx={{
                                        px: 2.5,
                                        pt: 2,
                                        pb: 1.75,
                                        background:
                                            'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.05) 100%)',
                                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(99,102,241,0.15)',
                                            display: 'flex',
                                        }}>
                                        <QuestionAnswerOutlinedIcon sx={{ color: '#818cf8', fontSize: 18 }} />
                                    </Box>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                                        Manager Questions
                                    </Typography>
                                    {unansweredCount > 0 && (
                                        <Box
                                            sx={{
                                                ml: 0.5,
                                                px: 1,
                                                py: 0.2,
                                                borderRadius: 10,
                                                bgcolor: BRAND_LUXE_PRIMARY,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: '#000',
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    lineHeight: 1.4,
                                                }}>
                                                {unansweredCount}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {clarifications.map(c => (
                                        <Box
                                            key={c._id}
                                            sx={{
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                border: `1px solid ${
                                                    c.is_answered ? 'rgba(52,211,153,0.2)' : 'rgba(99,102,241,0.25)'
                                                }`,
                                            }}>
                                            {/* Question */}
                                            <Box
                                                sx={{
                                                    p: 1.75,
                                                    bgcolor: c.is_answered
                                                        ? 'rgba(52,211,153,0.05)'
                                                        : 'rgba(99,102,241,0.07)',
                                                }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                                    <Box
                                                        sx={{
                                                            width: 7,
                                                            height: 7,
                                                            borderRadius: '50%',
                                                            bgcolor: c.is_answered ? '#34d399' : '#818cf8',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                        {c.from_email} · {fmtDateTime(c.created_at)}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ color: '#fff', fontSize: 13, lineHeight: 1.55 }}>
                                                    {c.message}
                                                </Typography>
                                            </Box>

                                            {/* Answer / reply box */}
                                            {c.is_answered ? (
                                                <Box
                                                    sx={{
                                                        px: 1.75,
                                                        py: 1.25,
                                                        borderTop: '1px solid rgba(52,211,153,0.12)',
                                                        bgcolor: 'rgba(52,211,153,0.03)',
                                                        display: 'flex',
                                                        gap: 1.25,
                                                    }}>
                                                    <CheckCircleOutlineIcon
                                                        sx={{ color: '#34d399', fontSize: 16, mt: 0.2, flexShrink: 0 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ color: '#34d399', fontSize: 11, mb: 0.3 }}>
                                                            Replied {c.replied_at ? fmtDateTime(c.replied_at) : ''}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: 'rgba(255,255,255,0.8)',
                                                                fontSize: 13,
                                                                lineHeight: 1.5,
                                                            }}>
                                                            {c.reply}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        px: 1.75,
                                                        py: 1.25,
                                                        borderTop: '1px solid rgba(99,102,241,0.15)',
                                                        display: 'flex',
                                                        gap: 1,
                                                    }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Type your reply…"
                                                        value={replyTexts[c._id] || ''}
                                                        onChange={e =>
                                                            setReplyTexts(prev => ({
                                                                ...prev,
                                                                [c._id]: e.target.value,
                                                            }))
                                                        }
                                                        sx={{
                                                            input: { color: '#fff', fontSize: 13 },
                                                            '& .MuiOutlinedInput-root': {
                                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'rgba(255,255,255,0.2)',
                                                                },
                                                                '&.Mui-focused fieldset': { borderColor: '#818cf8' },
                                                            },
                                                            '& .MuiInputBase-input::placeholder': {
                                                                color: BRAND_LUXE_MUTED,
                                                            },
                                                        }}
                                                    />
                                                    <ManageGoldButton
                                                        onClick={() => onSendReply(c._id)}
                                                        disabled={
                                                            replyingToClarification || !(replyTexts[c._id] || '').trim()
                                                        }
                                                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        Send reply
                                                    </ManageGoldButton>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </ManageSurface>
                        ) : null}
                    </Box>
                )}
            </Box>
            <Dialog
                open={Boolean(confirmAction)}
                onClose={() => !mutatingContract && setConfirmAction(null)}
                PaperProps={{ sx: { bgcolor: '#1a1a1a', color: '#fff', borderRadius: 2, minWidth: 320 } }}>
                <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    {confirmAction === 'freeze' ? 'Pause contract?' : 'Void contract?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: BRAND_LUXE_MUTED }}>
                        {confirmAction === 'freeze'
                            ? 'This will pause the contract and suspend escrow locking until it is reactivated.'
                            : 'This will permanently void the contract. This cannot be undone — you can renew as a new draft.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setConfirmAction(null)}
                        disabled={mutatingContract}
                        sx={{ color: BRAND_LUXE_MUTED, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirmAction}
                        disabled={mutatingContract}
                        sx={{
                            color: confirmAction === 'void' ? '#ff7351' : BRAND_LUXE_PRIMARY,
                            textTransform: 'none',
                        }}>
                        {mutatingContract ? 'Processing…' : confirmAction === 'freeze' ? 'Pause' : 'Void'}
                    </Button>
                </DialogActions>
            </Dialog>
        </BrandManageGate>
    );
}
